import {
    Box,
    Divider,
    ListItemIcon,
    ListItemText,
    MenuItem,
} from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { Link } from 'react-router-dom';
import { useUserMenu, usePermissions } from 'react-admin';
import { ADMIN_ENTRIES } from '../layout/menuEntries';

// Faint primary-tinted background shared by every row of the admin panel, so
// the block reads as one grouped "privileged" zone. Derived from the theme
// primary so it adapts to both light and dark modes.
const tintBg = (theme: Theme) => alpha(theme.palette.primary.main, 0.06);

export const AdminMenu = () => {
    const { permissions } = usePermissions();
    const { onClose } = useUserMenu() ?? {};

    if (permissions !== 'admin') return null;

    // The subheader and items are siblings of the menu (not wrapped in a Box)
    // so MenuList keeps arrow-key focus working — same pattern as the existing
    // <Divider />. The contiguous tint + rounded end corners fake the panel.
    return (
        <>
            <Box
                component="li"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mx: 1,
                    mt: 1,
                    px: 2,
                    pt: 1,
                    pb: 0.5,
                    bgcolor: tintBg,
                    borderTopLeftRadius: 8,
                    borderTopRightRadius: 8,
                    color: 'text.secondary',
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    userSelect: 'none',
                    listStyle: 'none',
                }}
            >
                <AdminPanelSettingsIcon aria-hidden sx={{ fontSize: 15 }} />
                Admin
            </Box>
            {ADMIN_ENTRIES.map((entry, i) => {
                const Icon = entry.icon;
                const last = i === ADMIN_ENTRIES.length - 1;
                const shape = {
                    mx: 1,
                    bgcolor: tintBg,
                    ...(last
                        ? {
                              mb: 0.5,
                              borderBottomLeftRadius: 8,
                              borderBottomRightRadius: 8,
                          }
                        : {}),
                };
                if (entry.disabled) {
                    return (
                        <MenuItem
                            key={entry.id}
                            disabled
                            sx={{
                                ...shape,
                                // Keep the panel tint at full strength; grey only
                                // the contents so the row still reads as disabled.
                                '&.Mui-disabled': {
                                    opacity: 1,
                                    color: 'text.disabled',
                                },
                                '& .MuiListItemIcon-root': {
                                    color: 'text.disabled',
                                },
                            }}
                        >
                            <ListItemIcon>
                                <Icon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>{entry.label}</ListItemText>
                        </MenuItem>
                    );
                }
                return (
                    <MenuItem
                        key={entry.id}
                        component={Link}
                        to={entry.to!}
                        onClick={onClose}
                        sx={shape}
                    >
                        <ListItemIcon>
                            <Icon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>{entry.label}</ListItemText>
                    </MenuItem>
                );
            })}
            <Divider />
        </>
    );
};
